if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cartDrawer = document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        let formData = new FormData(this.form);

        let allSections = [];
        if (this.cartDrawer) {
          allSections.push(
            ...this.cartDrawer
              .getSectionsToRender()
              .map((section) => section.id)
          );
        }

        if (allSections.length > 0) {
          formData.append("sections", allSections.join(","));
        }

        // The subscription widget owns the quantity on a subscription line, and
        // says so on the form as data-csw-quantity. Applying it here rather than
        // trusting the fields is what makes it stick: Kaching Bundles prepends a
        // quantity input of its own to this form on every click of this button —
        // whether or not its bars are on screen — carrying whichever deal bar was
        // last selected. The widget's own input is form-associated from outside
        // the <form>, so Kaching's serialises after it and wins. formData.set
        // replaces every entry of that name with one value, so the duplicate
        // stops mattering, and nothing runs between here and the fetch.
        //
        // Its bundle line property goes with it: that marks the line as Kaching's
        // and would hand our subscription line to the app's cart-side rewriting.
        const cswQuantity = this.form.dataset.cswQuantity;
        const isSubscription = String(
          formData.get("selling_plan") || ""
        ).trim();

        if (isSubscription && cswQuantity) {
          formData.set("quantity", cswQuantity);
          formData.delete("properties[__kaching_bundles]");
        }

        const mainVariantId = formData.get("id");
        formData = await this.withAutoAddItems(formData);

        config.body = formData;

        // Kaching Bundles patches window.fetch and rewrites /cart/add. When one
        // of its deal blocks is live on the page it registers a short-lived
        // "fallback deal" on every click of this button — visible block or not —
        // and its merge then drops our line for that product and substitutes its
        // own items. That branch does not carry selling_plan across, so a
        // subscription add would silently land in the cart as a one-time
        // purchase, with no error.
        //
        // kaching_bundles=true is Kaching's own skip marker: its
        // _isInterceptableUrl() ignores any cart/add URL containing it, so that
        // requests it has already merged aren't merged a second time. We borrow
        // it to mean "this add is final". It's an internal string rather than a
        // documented API, so if a Kaching update ever drops it the symptom is a
        // subscription add arriving without a selling_plan_allocation.
        const addUrl = isSubscription
          ? `${routes.cart_add_url}${
              routes.cart_add_url.includes("?") ? "&" : "?"
            }kaching_bundles=true`
          : routes.cart_add_url;

        try {
          const response = await fetch(addUrl, config).then((response) =>
            response.json()
          );

          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: "product-form",
              productVariantId: mainVariantId,
              errors: response.errors || response.description,
              message: response.message,
            });
            this.handleErrorMessage(response.description);

            const soldOutMessage =
              this.submitButton.querySelector(".sold-out-message");
            if (!soldOutMessage) return;
            this.submitButton.setAttribute("aria-disabled", true);
            this.submitButtonText.classList.add("hidden");
            soldOutMessage.classList.remove("hidden");
            this.error = true;
            return;
          } else if (!this.cartDrawer) {
            window.location = window.routes.cart_url;
            return;
          }

          if (!this.error) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "product-form",
              productVariantId: mainVariantId,
              cartData: response,
              // The bundled sections below are applied via
              // updateContentsSilently — no listener needs to refetch.
              rendered: true,
            });
          }

          this.error = false;

          const quickAddModal = this.closest("quick-add-modal");
          if (quickAddModal) {
            quickAddModal.hide(true);
          }

          if (this.cartDrawer) {
            this.cartDrawer.updateContentsSilently(response);
            this.openCartDrawer();
            this.setCartDrawerLoading(true);
          }

          await checkAndAddGWP();
        } catch (e) {
          console.error(e);
        } finally {
          updateCounters();
          this.submitButton.classList.remove("loading");
          if (this.cartDrawer && this.cartDrawer.classList.contains("is-empty"))
            this.cartDrawer.classList.remove("is-empty");
          if (!this.error) this.submitButton.removeAttribute("aria-disabled");
          this.querySelector(".loading__spinner").classList.add("hidden");
          this.setCartDrawerLoading(false);
        }
      }

      // "Auto add product" blocks (main-product) mark products to go into the
      // cart alongside this one. They are sent in the same request, so the
      // cart never sits with one and not the other, which means switching the
      // body to /cart/add's items[] form: the page's line first, exactly as
      // the form had it, then one of each auto-add product.
      //
      // Once per cart: a product already added this way is skipped, so a
      // second bundle doesn't bring a second copy. The lines carry
      // _auto_added_with (the page's product id), which cart.js uses to take
      // them out again when that product leaves the cart. Returns the form
      // data untouched when there is nothing to add.
      async withAutoAddItems(formData) {
        const markers = Array.from(this.form.elements).filter(
          (el) => el.dataset && el.dataset.autoAddVariant
        );
        if (!markers.length) return formData;

        let cart;
        try {
          cart = await fetch(`${routes.cart_url}.js`).then((res) => res.json());
        } catch (e) {
          return formData;
        }
        const alreadyAdded = new Set(
          (cart.items || [])
            .filter((item) => item.properties && item.properties._auto_added_with)
            .map((item) => String(item.variant_id))
        );
        const toAdd = markers.filter(
          (el) => !alreadyAdded.has(String(el.dataset.autoAddVariant))
        );
        if (!toAdd.length) return formData;

        const body = new FormData();
        body.append("items[0][id]", formData.get("id"));
        body.append("items[0][quantity]", formData.get("quantity") || "1");
        for (const [key, value] of formData.entries()) {
          if (key === "selling_plan" && String(value).trim()) {
            body.append("items[0][selling_plan]", value);
          } else if (key.startsWith("properties[")) {
            body.append(`items[0][properties]${key.slice("properties".length)}`, value);
          } else if (key === "sections" || key === "sections_url") {
            body.append(key, value);
          }
        }
        toAdd.forEach((el, i) => {
          const n = i + 1;
          body.append(`items[${n}][id]`, el.dataset.autoAddVariant);
          body.append(`items[${n}][quantity]`, "1");
          body.append(`items[${n}][properties][_auto_added_with]`, el.dataset.autoAddParent);
        });
        return body;
      }

      setCartDrawerLoading(isLoading) {
        if (!this.cartDrawer) return;

        this.cartDrawer.classList.toggle("is-loading", isLoading);
      }

      openCartDrawer() {
        if (!this.cartDrawer) return;

        this.cartDrawer.setActiveElement(this.submitButton);
        this.cartDrawer.open();
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}
